import React, { useCallback } from 'react'
import clsx from 'clsx'
import type { ResponsiveImageOptions } from 'lib/image/imageUtils'
import { getImageUrl } from 'lib/image/imageUtils'
import ListItemThumbnail from 'LuxKit/List/ListItemThumbnail'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import styled from 'styled-components'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import InteractiveZone from '../../InteractiveZone'
import { formatDecimal } from 'lib/format/formatNumber'
import { rem } from 'polished'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import { useIntl, FormattedMessage } from 'react-intl'

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const StyledClickableListItem = styled(ClickableListItem)`
  border-radius: ${props => props.theme.borderRadius.L};
  border: 1px solid ${props => props.theme.palette.neutral.default.seven};
  transition: background 0.15s ease, border-color 0.15s ease;

  &.correct {
    border: 1px solid ${props => props.theme.palette.neutral.default.seven};
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
    color: ${props => props.theme.palette.neutral.default.eight};

    &:hover {
      background: ${props => props.theme.palette.highlight.primary.normalBackground};
      border-color: ${props => props.theme.palette.highlight.primary.normalForeground};
    }
  }

  &.wrong {
    border: 1px solid ${props => props.theme.palette.messaging.critical.normalForeground};
    background: rgba(239, 80, 95, 0.10);

    &:hover {
      background: rgba(239, 80, 95, 0.20);
      border-color: ${props => props.theme.palette.messaging.critical.darkForeground};
    }
  }
`

const IconBlock = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  padding: ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.default.six};
  display: flex;
  align-items: center;
  justify-content: center;
`

const ThumbnailImageParams: ResponsiveImageOptions = { width: 96, height: 96, quality: 'good' }

interface Destination {
  id: string
  name: string
  imageId?: string
  viewCount: number
}

interface Props {
  destination: Destination
  phase: 'question' | 'answer'
  isCorrect: boolean
  isSelected: boolean
  onSelect?: (id: string) => void
}

function DestinationListItem({
  destination,
  phase,
  isCorrect,
  isSelected,
  onSelect,
}: Props) {
  const intl = useIntl()
  const isWrong = phase === 'answer' && isSelected && !isCorrect

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect?.(destination.id)
  }, [onSelect, destination.id])

  const thumbnail = (
    <ListItemThumbnail gutter="none">
      {destination.imageId && (
        <ThumbnailImage
          src={getImageUrl(destination.imageId, ThumbnailImageParams)}
          alt={destination.name}
        />
      )}
      {!destination.imageId && (
        <IconBlock>
          <LineMapMarkerIcon />
        </IconBlock>
      )}
    </ListItemThumbnail>
  )

  const title = <Heading variant="heading3"><i>{destination.name}</i></Heading>

  const correctSubtitle = <FormattedMessage
    id="2AUInnYgza"
    defaultMessage="Viewed {count} times \u00B7 <b>Most viewed</b>"
    description="Subtitle for destination showing view count and most viewed badge"
    values={{
      count: formatDecimal(destination.viewCount),
      b: (chunks: React.ReactNode) => <b>{chunks}</b>,
    }}
  />
  const normalSubtitle = intl.formatMessage({ id: '9NqrZdJvTf', defaultMessage: 'Viewed {count} times', description: 'Subtitle for destination showing view count in the year-in-review feature' }, { count: formatDecimal(destination.viewCount) })

  if (phase === 'question') {
    return (
      <InteractiveZone>
        <StyledClickableListItem
          onClick={handleClick}
          size="large"
          startIcon={thumbnail}
          title={title}
        />
      </InteractiveZone>
    )
  }

  return (
    <InteractiveZone>
      <StyledClickableListItem
        onClick={handleClick}
        size="large"
        className={clsx({
          correct: isCorrect,
          wrong: isWrong,
        })}
        startIcon={thumbnail}
        endIcon={<BodyText variant="small" wrap="no-wrap" underline>{intl.formatMessage({ id: 'IBpS+kTsev', defaultMessage: 'View offers', description: 'Link text to view offers for destination on the offer page' })}</BodyText>}
        title={title}
        subtitle={
          <Caption variant="medium" colour={isCorrect ? 'neutral-eight' : 'neutral-one'}>
            {isCorrect ? correctSubtitle : normalSubtitle}
          </Caption>
        }
      />
    </InteractiveZone>
  )
}

export default React.memo(DestinationListItem)
